import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Box, Heading, Spinner, Alert, AlertIcon, List, ListItem, Badge, VStack, Text } from '@chakra-ui/react';
import { useAuth } from '../../utils/AuthContext';
import { useToast } from '@chakra-ui/react';

const ExamResponse = () => {
  const { userId, paperSetId } = useParams();
  const { auth } = useAuth();
  const [responseDetails, setResponseDetails] = useState([]);
  const [userInfo, setUserInfo] = useState({});
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();

  useEffect(() => {
    const fetchResponseDetails = async () => {
      if (!userId || !paperSetId) {
        setError('Missing userId or paperSetId');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Results/examresponses/${userId}/${paperSetId}`, {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        });
        const data = response.data;
        setUserInfo({
          empName: data.empName,
          designation: data.designation,
          city: data.city,
          questionCount: data.questionCount,
          correctAnswers: data.correctAnswers,
          wrongAnswers: data.wrongAnswers,
          notAttemptedQuestions: data.notAttemptedQuestions
        });
        setResponseDetails(data.responseDetails.$values || data.responseDetails);
      } catch (error) {
        setError('Error fetching exam response details');
        console.error('Error fetching exam response details:', error);
        toast({
          title: "Error fetching exam response details",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    const fetchAllQuestions = async () => {
      if (!paperSetId) return;
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/PaperSet/${paperSetId}/questions`, {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        });
        setQuestions(response.data.$values || response.data);
      } catch (error) {
        console.error('Error fetching questions:', error);
      }
    };

    const fetchData = async () => {
      await fetchResponseDetails();
      await fetchAllQuestions();
    };

    fetchData();
  }, [auth.token, userId, paperSetId, toast]);

  const getNotAttemptedQuestions = () => {
    const attemptedQuestionIds = new Set(responseDetails.map(detail => detail.questionId));
    return questions.filter(question => !attemptedQuestionIds.has(question.questionId));
  };

  const notAttemptedQuestions = getNotAttemptedQuestions();

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box p={4}>
      <Heading mb={4}>Exam Response Details</Heading>
      <Box mb={4}>
        <Text><strong>Employee Name:</strong> {userInfo.empName}</Text>
        <Text><strong>Designation:</strong> {userInfo.designation}</Text>
        <Text><strong>City:</strong> {userInfo.city}</Text>
        <Text><strong>Total Questions:</strong> {userInfo.questionCount}</Text>
        <Text><strong>Correct Answers:</strong> {userInfo.correctAnswers}</Text>
        <Text><strong>Wrong Answers:</strong> {userInfo.wrongAnswers}</Text>
        <Text><strong>Not Attempted Questions:</strong> {notAttemptedQuestions.length}</Text>
      </Box>
      <VStack spacing={4}>
        {responseDetails.map((detail, index) => (
          <Box key={index} p={4} borderWidth={1} borderRadius="md" w="100%">
            <Heading size="md" mb={2}>Question {index + 1}</Heading>
            <Text mb={2}><strong>Question:</strong> {detail.questionText}</Text>
            <Box mb={2}>
              <strong>Options:</strong>
              <List spacing={1}>
                {(detail.options.$values || []).map((option, idx) => (
                  <ListItem key={idx} color={option.isCorrect ? 'green.500' : (option.optionId === detail.selectedOptionId ? 'red.500' : 'black')}>
                    {String.fromCharCode(65 + idx)}. {option.optionText} {option.isCorrect && <Badge colorScheme="green">Correct</Badge>}
                  </ListItem>
                ))}
              </List>
            </Box>
            <Text><strong>User Response:</strong> {detail.userResponseText}</Text>
            <Text><strong>Correct Answer:</strong> {detail.correctAnswerText}</Text>
          </Box>
        ))}
      </VStack>
      <Heading mt={8} mb={4}>Not Attempted Questions</Heading>
      <VStack spacing={4}>
        {notAttemptedQuestions.map((question, index) => (
          <Box key={index} p={4} borderWidth={1} borderRadius="md" w="100%">
            <Heading size="md" mb={2}>Question {index + 1}</Heading>
            <Text mb={2}><strong>Question:</strong> {question.questionText}</Text>
            <Box mb={2}>
              <strong>Options:</strong>
              <List spacing={1}>
                {(question.options.$values || []).map((option, idx) => (
                  <ListItem key={idx}>
                    {String.fromCharCode(65 + idx)}. {option.optionText}
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        ))}
      </VStack>
    </Box>
  );
};

export default ExamResponse;
